import {
  Feature,
  FeatureCollection,
  LineString,
  MultiPolygon,
  Point,
  Polygon,
} from 'geojson'
import { SpaceType } from '~/packages/general/variable/spaceTime.types'
import { AlertType } from '~/packages/general/variable/variables.typing'
import { FeatureStyleProperties } from '~/components/map/mapStyle'

export interface StatsItem {
  value?: number
  valueSum?: number
  valueCount?: number
  geoId?: string | number
  geoType: SpaceType
  dateTime?: string
  alert?: AlertType
  numerator?: number
  denominator?: number
}

export type GeoItemWithShape = {
  id: string | number
  name: string
  shape: Polygon | MultiPolygon
  type: SpaceType
  subtitle?: string
}

export type GeoItemWithPath = {
  id: string | number
  name: string
  type: SpaceType
  path: LineString
  subtitle?: string
}

export type GeoItemWithPoint = {
  id: string | number
  name: string
  type: SpaceType
  point: Point
  subtitle?: string
}

export type Info = {
  value?: number
  name: string
  geoId?: string | number
  geoType: SpaceType
  alert?: AlertType
  numerator?: number
  denominator?: number
}

export type InfoAndStyle = Info & FeatureStyleProperties

export type FeatureWithInfo = Feature<Polygon | MultiPolygon | Point | LineString, Info>
export type FeatureCollectionWithInfo = FeatureCollection<
  Polygon | MultiPolygon | Point | LineString,
  Info
>

export type FeatureWithStyle = Feature<
  Polygon | MultiPolygon | Point | LineString,
  FeatureStyleProperties
>
export type FeatureCollectionWithStyle = FeatureCollection<
  Polygon | MultiPolygon | Point | LineString,
  FeatureStyleProperties
>

export type FeatureWithInfoAndStyle = Feature<
  Polygon | MultiPolygon | Point | LineString,
  InfoAndStyle
>
export type FeatureCollectionWithInfoAndStyle = FeatureCollection<
  Polygon | MultiPolygon | Point | LineString,
  InfoAndStyle
>
export type FeatureCollectionWithOptionalInfoAndStyle = FeatureCollection<
  Polygon | MultiPolygon | Point | LineString,
  Partial<InfoAndStyle>
>

export type GeoItem = GeoItemWithShape | GeoItemWithPath | GeoItemWithPoint

export default function geoAndStatsToFeatureCollection(
  statsItems: StatsItem[] = [],
  geoItems: GeoItem[] = [],
  {
    fillStatsItem = { value: 0 },
    mapStyle,
  }: {
    fillStatsItem?: Omit<StatsItem, 'geoId' | 'geoType'>
    mapStyle?: (props: Info) => FeatureStyleProperties
  } = {},
) {
  const statsItemsMap = statsItems.reduce(
    (acc, item) => {
      const key = `${item.geoId}-${item.geoType}`
      if (!acc[key]) {
        acc[key] = item
      }
      return acc
    },
    {} as Record<string, StatsItem>,
  )

  const features = geoItems.map(geoItem => {
    const key = `${geoItem.id}-${geoItem.type}`
    const statsItem = statsItemsMap[key] || fillStatsItem

    let geometry =
      'shape' in geoItem && geoItem.shape
        ? geoItem.shape
        : 'path' in geoItem && geoItem.path
          ? geoItem.path
          : (geoItem as GeoItemWithPoint).point

    const info = {
      value: statsItem.value,
      numerator: statsItem?.numerator ?? undefined,
      denominator: statsItem?.denominator ?? undefined,
      name: geoItem.name,
      geoId: geoItem.id,
      geoType: geoItem.type,
      alert: statsItem.alert,
    }
    const feature: FeatureWithInfoAndStyle = {
      type: 'Feature',
      properties: {
        ...info,
        ...(mapStyle ? mapStyle(info) : {}),
      },
      geometry: geometry,
    }

    return feature
  })

  const sortedFeaturesByZIndex = features.sort((a, b) => {
    const zIndexA = a.properties?.zIndex || 0
    const zIndexB = b.properties?.zIndex || 0
    return zIndexA - zIndexB
  })

  const featureCollection: FeatureCollectionWithInfoAndStyle = {
    type: 'FeatureCollection',
    features: sortedFeaturesByZIndex,
  }

  return featureCollection
}
